import { random } from 'lodash';
import { Spacing } from 'web-common/src/shared/styles';
import {
  AnimateConfettiType,
  ConfettiPhysicsType,
  ConfettoType,
  CreateConfettiPhysicsProps,
  CreateConfettiProps,
  GenerateConfettiProps,
  UpdateConfettoType,
} from './types';

export const createConfetto = (color: string): HTMLDivElement => {
  /* eslint-disable functional/immutable-data -- Assigning element styles */
  const element = document.createElement('div');
  element.style.background = color;
  element.style.width = Spacing.THREE_QUARTERS;
  element.style.height = Spacing.THREE_QUARTERS;
  element.style.borderRadius = Spacing.EIGHTH;
  element.style.position = 'absolute';
  return element;
  /* eslint-enable functional/immutable-data -- Assigning element styles */
};

export const updateConfetto = ({ confetto, progress, decay }: UpdateConfettoType) => {
  /* eslint-disable functional/immutable-data -- Assigning new confetti physics */
  /* eslint-disable no-param-reassign -- Assigning new confetti physics */
  confetto.physics.x += Math.cos(confetto.physics.angle2D) * confetto.physics.velocity;
  confetto.physics.y += Math.sin(confetto.physics.angle2D) * confetto.physics.velocity;
  confetto.physics.z += Math.sin(confetto.physics.angle3D) * confetto.physics.velocity;
  confetto.physics.wobble += 0.1;
  confetto.physics.velocity *= decay;
  confetto.physics.y += 3;
  confetto.physics.tiltAngle += 0.1;

  const { x, y, tiltAngle, wobble } = confetto.physics;
  const wobbleX = x + 10 * Math.cos(wobble);
  const wobbleY = y + 10 * Math.sin(wobble);
  const transform = `translate3d(${wobbleX}px, ${wobbleY}px, 0) rotate3d(1, 1, 1, ${tiltAngle}rad)`;
  confetto.element.style.transform = transform;
  confetto.element.style.opacity = `${1 - progress}`;
  /* eslint-enable functional/immutable-data -- Assigning new confetti physics */
  /* eslint-enable no-param-reassign -- Assigning new confetti physics */
};

export const createConfetti = ({ colors, numberOfParticles, root }: CreateConfettiProps) =>
  Array.from({ length: numberOfParticles }).map((_, index) => {
    const color = colors[index % colors.length];
    const confetto = createConfetto(color);

    if (root?.current) {
      root.current.appendChild(confetto);
    }
    return confetto;
  });

export const animateConfetti = ({ root, confetti, decay }: AnimateConfettiType) => {
  const totalTicks = 150;
  // eslint-disable-next-line functional/no-let -- Loop for dacy function
  let tick = 0;

  function update() {
    confetti.forEach((confetto) => updateConfetto({ confetto, progress: tick / totalTicks, decay }));

    tick += 1;
    if (tick < totalTicks) {
      requestAnimationFrame(update);
    } else {
      confetti.forEach((singleConfetti) => {
        if (root?.current) {
          return root.current.removeChild(singleConfetti.element);
        }
        return null;
      });
    }
  }
  requestAnimationFrame(update);
};

export const generateRandomPhysics = ({
  angle,
  spread,
  startVelocity,
}: CreateConfettiPhysicsProps): ConfettiPhysicsType => {
  const radAngle = angle * (Math.PI / 180);
  const radSpread = spread * (Math.PI / 180);
  return {
    x: 0,
    y: 0,
    z: 0,
    wobble: random(10, true),
    velocity: startVelocity * 0.6 + random(startVelocity, true),
    angle2D: -radAngle + (0.6 * radSpread - random(radSpread, true)),
    angle3D: -(Math.PI / 4) + random(Math.PI / 2, true),
    tiltAngle: random(Math.PI, true),
  };
};

export const generateConfetto = (element: HTMLDivElement, physics: ConfettiPhysicsType): ConfettoType => ({
  element,
  physics,
});

export const generateConfetti = ({
  colors,
  decay,
  angle,
  spread,
  startVelocity,
  numberOfParticles,
  root,
}: GenerateConfettiProps) => {
  const createdConfetti = createConfetti({ colors, numberOfParticles, root });
  const confetti = createdConfetti.map((element) =>
    generateConfetto(element, generateRandomPhysics({ angle, spread, startVelocity }))
  );
  animateConfetti({ root, confetti, decay });
  return confetti;
};
